import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAvailableAdminsThunk,
  getAdminsBySpecializationThunk,
  getBranchAdminsThunk,
  getAdminsByRoleThunk,
  getBulkAdminAvailabilityThunk,
} from "../../../../store/thunks/adminThunks";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Input from "../../../../components/common/Input";
import Dropdown from "../../../../components/common/Dropdown";
import {
  SearchIcon,
  UserGroupIcon,
  StarIcon,
  ClockIcon,
  LocationMarkerIcon,
  FilterIcon,
  RefreshIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";

const AdminSearchPanel = () => {
  const dispatch = useDispatch();
  const {
    availableAdmins,
    branchAdmins,
    specializedAdmins,
    loadingList,
    appointmentTypes,
    specializations,
  } = useSelector((state) => state.admin);

  const { branches } = useSelector((state) => state.branch);

  // Local state
  const [searchMode, setSearchMode] = useState("available"); // 'available', 'specialization', 'branch', 'role'
  const [filters, setFilters] = useState({
    branchCode: null,
    specialization: null,
    appointmentType: null,
    dateTime: new Date().toISOString(),
    role: null,
    minRating: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAdmins, setSelectedAdmins] = useState([]);

  // Convert branches to dropdown format
  const branchOptions = (branches || []).map((branch) => ({
    _id: branch.branchCode || branch.code,
    name: `${branch.name || branch.branchName} (${
      branch.branchCode || branch.code
    })`,
  }));

  // Convert specializations to dropdown format
  const specializationOptions = (specializations || []).map((spec) => ({
    _id: spec.value,
    name: spec.label,
  }));

  // Convert appointment types to dropdown format
  const appointmentTypeOptions = (appointmentTypes || []).map((type) => ({
    _id: type.value,
    name: type.label,
  }));

  // Role options
  const roleOptions = [
    { _id: "admin", name: "Admin" },
    { _id: "branch_manager", name: "Branch Manager" },
    { _id: "rate_manager", name: "Rate Manager" },
    { _id: "staff", name: "Staff" },
  ];

  // Load initial data
  useEffect(() => {
    if (searchMode === "available" && filters.branchCode) {
      handleSearch();
    }
  }, []);

  // Handle search
  const handleSearch = async () => {
    try {
      switch (searchMode) {
        case "available":
          if (filters.branchCode) {
            await dispatch(
              getAvailableAdminsThunk({
                branchCode: filters.branchCode._id,
                dateTime: filters.dateTime,
                appointmentType: filters.appointmentType?._id,
              })
            ).unwrap();
          }
          break;

        case "specialization":
          if (filters.specialization) {
            await dispatch(
              getAdminsBySpecializationThunk({
                specialization: filters.specialization._id,
                branchCode: filters.branchCode?._id,
              })
            ).unwrap();
          }
          break;

        case "branch":
          if (filters.branchCode) {
            await dispatch(
              getBranchAdminsThunk(filters.branchCode._id)
            ).unwrap();
          }
          break;

        case "role":
          if (filters.role) {
            await dispatch(
              getAdminsByRoleThunk({
                role: filters.role._id,
                branchCode: filters.branchCode?._id,
              })
            ).unwrap();
          }
          break;
      }
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  // Get current results based on search mode
  const getCurrentResults = () => {
    switch (searchMode) {
      case "available":
        return availableAdmins || [];
      case "specialization":
        return specializedAdmins || [];
      case "branch":
        return branchAdmins || [];
      case "role":
        return availableAdmins || [];
      default:
        return [];
    }
  };

  // Filter results by search query
  const filteredResults = getCurrentResults().filter((admin) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      admin.name?.toLowerCase().includes(query) ||
      admin.email?.toLowerCase().includes(query) ||
      admin.adminCode?.toLowerCase().includes(query)
    );
  });

  // Apply rating filter
  const finalResults = filteredResults.filter((admin) => {
    if (filters.minRating === 0) return true;
    return (admin.rating || 0) >= filters.minRating;
  });

  // Toggle admin selection
  const toggleAdminSelection = (adminCode) => {
    setSelectedAdmins((prev) =>
      prev.includes(adminCode)
        ? prev.filter((code) => code !== adminCode)
        : [...prev, adminCode]
    );
  };

  // Check bulk availability
  const checkBulkAvailability = async () => {
    if (selectedAdmins.length === 0) return;

    try {
      await dispatch(
        getBulkAdminAvailabilityThunk({
          adminCodes: selectedAdmins,
          dateTime: filters.dateTime,
        })
      ).unwrap();
      alert("Bulk availability check complete! Check console for results.");
    } catch (error) {
      console.error("Bulk check failed:", error);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      AVAILABLE: "success",
      BUSY: "warning",
      IN_APPOINTMENT: "info",
      ON_BREAK: "warning",
      OFF_DUTY: "default",
      AWAY: "danger",
    };
    return colors[status] || "default";
  };

  // Render admin card
  const renderAdminCard = (admin) => {
    const isSelected = selectedAdmins.includes(admin.adminCode);

    return (
      <div
        key={admin.adminCode}
        className={`bg-white border-2 rounded-lg p-4 transition-all ${
          isSelected
            ? "border-primary ring-2 ring-primary ring-offset-1"
            : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 flex-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleAdminSelection(admin.adminCode)}
              className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate">
                  {admin.name}
                </h3>
                {admin.currentStatus && (
                  <Badge
                    variant={getStatusColor(admin.currentStatus)}
                    className="text-xs"
                  >
                    {admin.currentStatus}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600 truncate">{admin.email}</p>
              <p className="text-xs text-gray-500 mt-1">{admin.adminCode}</p>
            </div>
          </div>

          {admin.rating > 0 && (
            <div className="flex items-center gap-1 text-yellow-600">
              <StarIcon className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium">{admin.rating}</span>
            </div>
          )}
        </div>

        {/* Specializations */}
        {admin.specializations && admin.specializations.length > 0 && (
          <div className="mb-2">
            <div className="flex flex-wrap gap-1">
              {admin.specializations.slice(0, 3).map((spec, idx) => (
                <Badge key={idx} variant="info" className="text-xs">
                  {spec}
                </Badge>
              ))}
              {admin.specializations.length > 3 && (
                <Badge variant="default" className="text-xs">
                  +{admin.specializations.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-gray-600 mt-3 pt-3 border-t">
          {admin.totalAppointments > 0 && (
            <div className="flex items-center gap-1">
              <CheckCircleIcon className="w-4 h-4" />
              <span>{admin.totalAppointments} appointments</span>
            </div>
          )}
          {admin.acceptsAppointments !== undefined && (
            <div className="flex items-center gap-1">
              <ClockIcon className="w-4 h-4" />
              <span>
                {admin.acceptsAppointments ? "Accepting" : "Not accepting"}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render search mode tabs
  const searchModes = [
    { id: "available", label: "Available Now", icon: ClockIcon },
    { id: "specialization", label: "By Specialization", icon: StarIcon },
    { id: "branch", label: "By Branch", icon: LocationMarkerIcon },
    { id: "role", label: "By Role", icon: UserGroupIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center gap-3">
          <SearchIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Find Admins</h2>
            <p className="text-sm text-gray-600">
              Search and filter admins by availability, specialization, or
              location
            </p>
          </div>
        </div>
      </div>

      {/* Search Mode Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {searchModes.map((mode) => {
          const Icon = mode.icon;
          return (
            <button
              key={mode.id}
              onClick={() => setSearchMode(mode.id)}
              className={`p-3 rounded-lg border-2 transition-all ${
                searchMode === mode.id
                  ? "border-primary bg-blue-50 text-primary"
                  : "border-gray-200 hover:border-gray-300 bg-white text-gray-700"
              }`}
            >
              <Icon className="w-5 h-5 mx-auto mb-1" />
              <p className="text-xs font-medium">{mode.label}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4 border">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <FilterIcon className="w-4 h-4 text-primary" />
          Search Filters
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Branch Filter - shown for all modes */}
          <Dropdown
            label="Branch"
            options={branchOptions}
            selected={filters.branchCode}
            onChange={(branch) =>
              setFilters({ ...filters, branchCode: branch })
            }
            placeholder="Select branch"
            clearable
            required={searchMode === "available" || searchMode === "branch"}
          />

          {/* Mode-specific filters */}
          {searchMode === "available" && (
            <>
              <Dropdown
                label="Appointment Type"
                options={appointmentTypeOptions}
                selected={filters.appointmentType}
                onChange={(type) =>
                  setFilters({ ...filters, appointmentType: type })
                }
                placeholder="Any type"
                clearable
              />
              <Input
                label="Date & Time"
                type="datetime-local"
                value={filters.dateTime.slice(0, 16)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    dateTime: new Date(e.target.value).toISOString(),
                  })
                }
              />
            </>
          )}

          {searchMode === "specialization" && (
            <Dropdown
              label="Specialization"
              options={specializationOptions}
              selected={filters.specialization}
              onChange={(spec) =>
                setFilters({ ...filters, specialization: spec })
              }
              placeholder="Select specialization"
              required
              clearable
            />
          )}

          {searchMode === "role" && (
            <Dropdown
              label="Role"
              options={roleOptions}
              selected={filters.role}
              onChange={(role) => setFilters({ ...filters, role })}
              placeholder="Select role"
              required
              clearable
            />
          )}

          {/* Min Rating Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Rating
            </label>
            <div className="flex gap-2">
              {[0, 3, 4, 4.5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => setFilters({ ...filters, minRating: rating })}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                    filters.minRating === rating
                      ? "border-primary bg-blue-50 text-primary"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  {rating === 0 ? "Any" : `${rating}★+`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search Button */}
        <div className="mt-4 flex gap-3">
          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={loadingList}
            className="flex-1"
          >
            <SearchIcon className="w-4 h-4 mr-2" />
            {loadingList ? "Searching..." : "Search Admins"}
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              setFilters({
                branchCode: null,
                specialization: null,
                appointmentType: null,
                dateTime: new Date().toISOString(),
                role: null,
                minRating: 0,
              })
            }
          >
            <RefreshIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Text Search */}
      <div>
        <Input
          type="text"
          placeholder="Search by name, email, or admin code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={SearchIcon}
        />
      </div>

      {/* Bulk Actions */}
      {selectedAdmins.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">
                {selectedAdmins.length} admin
                {selectedAdmins.length > 1 ? "s" : ""} selected
              </p>
              <p className="text-sm text-gray-600">
                Perform bulk actions on selected admins
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={checkBulkAvailability}>
                Check Availability
              </Button>
              <Button variant="secondary" onClick={() => setSelectedAdmins([])}>
                Clear
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Search Results
            {finalResults.length > 0 && (
              <Badge variant="info" className="ml-2">
                {finalResults.length}
              </Badge>
            )}
          </h3>
          {finalResults.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const codes = finalResults.map((a) => a.adminCode);
                setSelectedAdmins(codes);
              }}
            >
              Select All
            </Button>
          )}
        </div>

        {loadingList ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Searching for admins...</p>
            </div>
          </div>
        ) : finalResults.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {finalResults.map((admin) => renderAdminCard(admin))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-2">No admins found</p>
            <p className="text-sm text-gray-500">
              Try adjusting your search filters or selecting a different search
              mode
            </p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Admin Search Tips</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Use "Available Now" to find admins ready for appointments</li>
              <li>Search by specialization to match customer needs</li>
              <li>Filter by branch to find location-specific admins</li>
              <li>Select multiple admins to check bulk availability</li>
              <li>Rating filter helps identify top-performing admins</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSearchPanel;
